import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal, MapPin, Eye } from "lucide-react";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

// Helper to resolve image URLs
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (url.startsWith('/uploads/')) {
    return `${backendUrl}/api${url}`;
  }
  return `${backendUrl}${url}`;
};

// Before/After Slider Component
function BeforeAfterSlider({ beforeUrl, afterUrl, title, yearBefore, yearAfter }) {
  const containerRef = useRef(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100);
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);
  const handleMouseMove = (e) => {
    if (isDragging) handleMove(e.clientX);
  };
  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div
      ref={containerRef}
      className="relative aspect-[16/10] rounded-2xl overflow-hidden cursor-ew-resize select-none"
      onMouseDown={handleMouseDown}
      onTouchMove={handleTouchMove}
    >
      {/* After image (background) */}
      <img
        src={getImageUrl(afterUrl)}
        alt="Después"
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      
      {/* Before image (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img
          src={getImageUrl(beforeUrl)}
          alt="Antes"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ 
            width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%',
            maxWidth: 'none'
          }}
          draggable={false}
        />
      </div>
      
      {/* Slider line */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        {/* Slider handle */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center">
          <SlidersHorizontal className="h-6 w-6 text-primary" />
        </div>
      </div>
      
      {/* Labels */}
      <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-black/70 backdrop-blur-sm rounded-lg text-white text-sm font-medium">
        {yearBefore || "Antes"}
      </div>
      <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-black/70 backdrop-blur-sm rounded-lg text-white text-sm font-medium">
        {yearAfter || "Ahora"}
      </div>
    </div>
  );
}

function BeforeAfterCard({ item }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300"
    >
      <Link to={`/antes-ahora/${item.slug}`} data-testid={`ba-card-${item.slug}`}>
        <BeforeAfterSlider
          beforeUrl={item.before_image_url}
          afterUrl={item.after_image_url}
          yearBefore={item.year_before}
          yearAfter={item.year_after}
        />
        
        <div className="p-5">
          <h3 className="font-heading text-xl font-bold text-brand-text mb-2">
            {item.title}
          </h3>
          {item.description && (
            <p className="text-brand-textSecondary text-sm line-clamp-2 mb-3">
              {item.description}
            </p>
          )}
          <div className="flex items-center justify-between text-xs text-brand-textMuted">
            {item.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {item.location}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Eye className="h-3 w-3" />
              {item.view_count || 0} vistas
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function AntesAhoraPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await axios.get(`${API}/public/before-after?limit=50`);
        setItems(response.data.items);
      } catch (error) {
        console.error("Error fetching before/after items:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <PageHero
        section="antesahora"
        title="Antes y Ahora"
        subtitle="Descubre cómo ha cambiado Choluteca a través del tiempo con nuestras comparaciones fotográficas interactivas"
        badge="Comparaciones Históricas"
        badgeIcon={SlidersHorizontal}
      />

      {/* Items Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-3xl overflow-hidden animate-pulse">
                  <div className="aspect-[16/10] bg-gray-200" />
                  <div className="p-5 space-y-2">
                    <div className="h-5 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {items.map((item, index) => (
                <motion.div
                  key={item.item_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <BeforeAfterCard item={item} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <SlidersHorizontal className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No hay comparaciones aún</h3>
              <p className="text-gray-500">Pronto compartiremos fotos del antes y ahora de Choluteca</p>
            </div>
          )}
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
